import { useEffect, useState } from 'react';

/**
 * Hook to detect WebSocket reconnections.
 * Returns a counter that increments on each reconnection.
 * Use this to force re-subscription in other hooks.
 * 
 * Usage:
 * ```typescript
 * const reconnectCount = useWebSocketReconnect();
 * 
 * useEffect(() => {
 *   // Subscribe to data
 *   // Will re-run on reconnection
 * }, [reconnectCount]);
 * ```
 */

let reconnectCounter = 0;
const listeners = new Set<(count: number) => void>();

// Global function to notify all hooks of reconnection
function notifyReconnection() {
  reconnectCounter++;
  console.log(`[WebSocket] Reconnection detected, count: ${reconnectCounter}`);
  listeners.forEach(listener => listener(reconnectCounter));
}

// Register global notifier for Apollo Client to call
if (typeof window !== 'undefined') {
  (window as any).__notifyWebSocketReconnection = notifyReconnection;
}

export function useWebSocketReconnect(): number {
  const [count, setCount] = useState(reconnectCounter);

  useEffect(() => {
    const listener = (newCount: number) => setCount(newCount);
    listeners.add(listener);
    
    return () => {
      listeners.delete(listener);
    };
  }, []);

  return count;
}
